<template>
  <div class="questions-loading-step">
    <atom-spinner
      :animation-duration="1000"
      :size="62"
      :color="'var(--v-profileAnchorColor-base)'"
    />

    <h2>Generating your AI Tracker questions…</h2>
    <p class="questions-loading-step__time">This can take around 3 minutes.</p>
    <p class="questions-loading-step__description">
      We're creating real customer questions based on your brand, market, and category.
    </p>

    <v-progress-linear
      class="questions-loading-step__bar"
      color="profileAnchorColor"
      height="8"
      :value="(this.currentStepIndex + 1) * 18"
      rounded
    />

    <div class="questions-loading-step__current" aria-live="polite">
      <span class="questions-loading-step__pulse"></span>
      <span class="questions-loading-step__label">Current step:</span>
      <span class="questions-loading-step__status">{{ currentStepText }}</span>
    </div>

    <p class="questions-loading-step__note">
      Please keep this window open. We'll move you to the next step automatically.
    </p>
  </div>
</template>

<script>
import { AtomSpinner } from 'epic-spinners'

const STEP_ROTATION_MS = 15000
const STEP_TEXTS = [
  'Analyzing your brand and category…',
  'Finding customer-style questions…',
  'Creating recommendation, comparison, pricing, and review prompts…',
  'Removing duplicate questions…',
  'Preparing your questions for review…',
]

export default {
  name: 'QuestionsLoadingStep',
  components: {
    AtomSpinner,
  },
  data () {
    return {
      currentStepIndex: 0,
      stepTimer: null,
    }
  },
  computed: {
    currentStepText () {
      return STEP_TEXTS[this.currentStepIndex]
    },
  },
  mounted () {
    this.stepTimer = setInterval(this.advanceStep, STEP_ROTATION_MS)
  },
  beforeDestroy () {
    this.clearStepTimer()
  },
  methods: {
    advanceStep () {
      if (this.currentStepIndex >= STEP_TEXTS.length - 1) {
        this.clearStepTimer()
        return
      }

      this.currentStepIndex += 1
    },
    clearStepTimer () {
      if (this.stepTimer) {
        clearInterval(this.stepTimer)
        this.stepTimer = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.questions-loading-step {
  display: flex;
  width: 100%;
  min-height: 560px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--v-mainColor-base);
  text-align: center;
  padding: 16px 0;
}

.questions-loading-step h2 {
  color: var(--v-mainColor-base);
  font-size: 27px;
  font-weight: 800;
  line-height: 1.25;
  margin: 26px 0 10px;
}

.questions-loading-step__time {
  color: var(--v-profileAnchorColor-base);
  font-size: 16px;
  font-weight: 800;
  line-height: 1.4;
  margin: 0 0 16px;
}

.questions-loading-step__description {
  color: #9AA6A8;
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 30px;
  max-width: 360px;
}

.questions-loading-step__bar {
  width: 100%;
  margin-bottom: 24px;
}

.questions-loading-step__current {
  display: flex;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  color: var(--v-mainColor3-base);
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 28px;
  max-width: 360px;
}

.questions-loading-step__label {
  color: var(--v-mainColor-base);
  font-weight: 800;
}

.questions-loading-step__status {
  color: #9AA6A8;
}

.questions-loading-step__pulse {
  width: 8px;
  height: 8px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: var(--v-profileAnchorColor-base);
  animation: questionsLoadingPulse 1.2s ease-in-out infinite;
}

.questions-loading-step__note {
  width: 100%;
  max-width: 360px;
  border-top: 1px solid var(--v-gray12Color-base);
  color: #9AA6A8;;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  padding-top: 20px;
}

@keyframes questionsLoadingPulse {
  0%,
  100% {
    opacity: 0.35;
  }

  50% {
    opacity: 1;
  }
}
</style>
